import { useQueryClient } from '@tanstack/react-query';
import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { UNSAFE_DataRouterContext } from 'react-router';

import { getEnv } from '../../lib/env';
import FlotiqPlugins from '../../lib/flotiq-plugins/flotiqPluginsRegistry';

import { usePluginsSettings } from '../../hooks/api';
import { useAllSpaceConstraints } from '../../hooks/api/useAllSpaceConstraints';
import useSelectedSpace from '../../hooks/useSelectedSpace';

const USER_PLUGINS_PARAMS = {
  limit: 1000,
  page: 1,
};

export const useLoadPlugins = () => {
  const queryClient = useQueryClient();
  const dataRouterContext = use(UNSAFE_DataRouterContext);

  const { space, spaceLimits, spaceFeatureFlags } = useSelectedSpace();
  const [pluginsLoaded, setPluginsLoaded] = useState(!FlotiqPlugins.enabled());

  const { data: userPlugins, isFetching: userPluginsAreLoading } =
    usePluginsSettings(USER_PLUGINS_PARAMS);

  const { allConstraints, isLoading: allConstraintsLoading } =
    useAllSpaceConstraints(space);

  const systemPlugins = useMemo(
    () => JSON.parse(getEnv('VITE_SYSTEM_PLUGINS') || '[]'),
    [],
  );

  const officialPluginsLimitExceeded =
    spaceLimits?.official_plugins_limit > -1 &&
    spaceLimits?.official_plugins_limit <
      (allConstraints?.['official-plugins-count'] || 0);

  const customPluginsLimitExceeded =
    spaceLimits?.custom_plugins_limit > -1 &&
    spaceLimits?.custom_plugins_limit <
      (allConstraints?.['custom-plugins-count'] || 0);

  const pluginsExceeded =
    (spaceLimits?.price || 0) > -1 &&
    (officialPluginsLimitExceeded || customPluginsLimitExceeded);

  useEffect(() => {
    setPluginsLoaded(false);
  }, [space]);

  const loadPlugins = useCallback(() => {
    if (pluginsLoaded || userPluginsAreLoading || allConstraintsLoading) return;

    FlotiqPlugins.spaceId = space;
    FlotiqPlugins.router = dataRouterContext?.router;
    FlotiqPlugins.queryClient = queryClient;
    FlotiqPlugins.spaceFeatureFlags = spaceFeatureFlags;

    const enabledPlugins = pluginsExceeded
      ? []
      : userPlugins.filter(({ enabled }) => enabled);

    Promise.all(
      [...systemPlugins, ...enabledPlugins].map(({ id, url, settings }) => {
        if (!id || !url) return null;
        return FlotiqPlugins.loadPlugin(id, url, settings);
      }),
    ).then(() => {
      setPluginsLoaded(true);
    });
  }, [
    allConstraintsLoading,
    dataRouterContext?.router,
    pluginsExceeded,
    pluginsLoaded,
    queryClient,
    space,
    systemPlugins,
    userPlugins,
    userPluginsAreLoading,
    spaceFeatureFlags,
  ]);

  return { pluginsLoading: !pluginsLoaded, loadPlugins };
};
